import type { ReactNode } from "react";
import { statusBadgeClass, statusLabel } from "@/lib/listFormat";

// A presentational list page styled to match the Submissions tab (header +
// toolbar + table + pager). Used by the scaffold tabs (Quotes, Binders,
// Policies, Declinations) to look functional without being wired: rows aren't
// links, and the toolbar/pager/actions are inert.
export interface ListRow {
  id: string;
  account: string;
  coverage: string;
  date: string;
  status: string;
  carrier: string | null;
}

interface ListScaffoldProps {
  title: string;
  subtitle: string;
  /** First-column header (e.g. "Quote", "Binder", "Policy", "Reference"). */
  idLabel: string;
  /** Date-column header (default "Created"). */
  dateLabel?: string;
  /** Optional inert primary action label (e.g. "New quote"). */
  actionLabel?: string;
  /** Noun used in the "Showing 1 to N of N …" line. */
  noun: string;
  rows: ListRow[];
}

export function ListScaffold({
  title,
  subtitle,
  idLabel,
  dateLabel = "Created",
  actionLabel,
  noun,
  rows,
}: ListScaffoldProps) {
  return (
    <section className="flex w-full flex-col gap-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-brk-dim">{subtitle}</p>
        </div>
        {actionLabel && (
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong"
          >
            <span aria-hidden="true" className="text-base leading-none">
              +
            </span>
            {actionLabel}
          </button>
        )}
      </div>

      {/* Toolbar — presentational only. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brk-dim"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="m20 20-3.2-3.2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <input
            type="search"
            aria-label={`Search ${noun}`}
            placeholder={`Search ${noun}…`}
            className="w-full rounded-md border border-brk-border bg-brk-panel py-2 pl-9 pr-3 text-sm text-brk-text placeholder:text-brk-dim focus:border-brk-accent focus:outline-none focus:ring-2 focus:ring-brk-accent/30"
          />
        </div>
        <FilterButton label="All statuses" />
        <FilterButton label="All coverages" />
        <FilterButton label="Date range" calendar />
        <button
          type="button"
          aria-label="More filters"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-brk-border bg-brk-panel text-brk-dim transition-colors hover:bg-brk-bg"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-brk-border bg-brk-panel">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-left text-sm">
            <thead className="border-b border-brk-border text-xs font-semibold uppercase tracking-wide text-brk-dim">
              <tr>
                <SortHeader>{idLabel}</SortHeader>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Coverage</th>
                <SortHeader>{dateLabel}</SortHeader>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Carrier</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-brk-border transition-colors hover:bg-brk-bg/60"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-brk-accent">
                    {r.id}
                  </td>
                  <td className="px-4 py-3 font-medium text-brk-text">
                    {r.account}
                  </td>
                  <td className="px-4 py-3 text-brk-dim">{r.coverage}</td>
                  <td className="px-4 py-3 text-brk-dim">{r.date}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                        r.status,
                      )}`}
                    >
                      {statusLabel(r.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-brk-dim">{r.carrier ?? "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <span
                      aria-hidden="true"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-brk-dim"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <circle cx="5" cy="12" r="1.6" />
                        <circle cx="12" cy="12" r="1.6" />
                        <circle cx="19" cy="12" r="1.6" />
                      </svg>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brk-border px-4 py-3 text-sm text-brk-dim">
          <span>
            Showing 1 to {rows.length} of {rows.length} {noun}
          </span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brk-border text-brk-dim opacity-50"
              >
                ‹
              </span>
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brk-accent font-medium text-brk-accent">
                1
              </span>
              <span
                aria-hidden="true"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brk-border text-brk-dim opacity-50"
              >
                ›
              </span>
            </div>
            <FilterButton label="10 per page" />
          </div>
        </div>
      </div>
    </section>
  );
}

function FilterButton({
  label,
  calendar,
}: {
  label: string;
  calendar?: boolean;
}) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-2 rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-sm text-brk-text transition-colors hover:bg-brk-bg"
    >
      {calendar && (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" />
        </svg>
      )}
      {label}
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="text-brk-dim"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  );
}

function SortHeader({ children }: { children: ReactNode }) {
  return (
    <th className="px-4 py-3">
      <span className="inline-flex items-center gap-1">
        {children}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="text-brk-dim/70"
        >
          <path d="m8 9 4-4 4 4M8 15l4 4 4-4" />
        </svg>
      </span>
    </th>
  );
}
